import * as React from "react";
import { pr, george, ui, observer } from "aaa";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { safeWindowOpen } from "util/helpers";
import { AppStoreRedirectStore } from "./AppStoreRedirectStoreNew";
import { GdsFakeActionButton } from "components/common/GdsFakeActionButton";
import { customColors } from "muiTheme";
import { parse } from "query-string";
import { IconGeorgeApp } from "components/util/primitives";

const FOOTER_HEIGHT = 64;
const DEFAULT_MARGIN = 40;
const WEB_QUERY_PARAM = "web-deeplink";
const SESSION_STORAGE_KEY = "george-web-deeplink-url";

const validateAndBuildDeeplinkUrl = (pathParam: string): string | null => {
    try {
        const decodedPath = decodeURIComponent(pathParam).trim();

        if (decodedPath.startsWith('//')) {
            console.error('Invalid web-deeplink: protocol-relative URLs not allowed');
            return null;
        }

        // Check for URL protocol schemes (e.g., http:, https:, ftp:, javascript:, data:, etc.)
        if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(decodedPath)) {
            console.error('Invalid web-deeplink: URLs with protocols not allowed');
            return null;
        }

        if (decodedPath.includes('://')) {
            console.error('Invalid web-deeplink: must be a path, not a URL');
            return null;
        }

        const cleanPath = decodedPath.startsWith('/') ? decodedPath.slice(1) : decodedPath;
        if (!cleanPath.trim()) {
            console.error('Invalid web-deeplink: path cannot be empty');
            return null;
        }

        return `${externalInterfaceStore.georgeBaseUrl}/#${cleanPath}`;
    } catch (error) {
        console.error('Failed to decode web-deeplink parameter', error);
        return null;
    }
};

const getWebDeeplinkUrl = (): string | null => {
    const storedUrl = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (storedUrl) {
        return storedUrl;
    }

    const queryParams = parse(window.location.search);
    const webDeeplinkParam = queryParams[WEB_QUERY_PARAM];

    if (typeof webDeeplinkParam === 'string' && webDeeplinkParam.trim()) {
        const validUrl = validateAndBuildDeeplinkUrl(webDeeplinkParam);
        if (validUrl) {
            sessionStorage.setItem(SESSION_STORAGE_KEY, validUrl);
            return validUrl;
        }
    }

    return null;
};

const clearWebDeeplinkFromUrl = (): void => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    if (params.has(WEB_QUERY_PARAM)) {
        params.delete(WEB_QUERY_PARAM);
        const newSearch = params.toString();
        const newUrl = `${url.pathname}${newSearch ? `?${newSearch}` : ''}${url.hash}`;
        window.history.replaceState({}, '', newUrl);
    }
};

const LandingPageHeader = ({ isMobile }: { isMobile: boolean }) => (
    <pr.HeaderBackground
        style={{
            display: "flex",
            justifyContent: isMobile ? "center" : "space-between",
            flexWrap: "wrap",
            alignItems: "center",
            minHeight: 235,
            height: "auto",
            backgroundColor: customColors.georgeOrange,
        }}
    >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "start", gap: 8, padding: 24 }}>
            <pr.P_24_TITLE style={{ textAlign: "center", color: "white", fontWeight: 600 }}>{ui.__("appStoreRedirect.landingPage.title")}</pr.P_24_TITLE>
            <pr.P_16_TITLE style={{ textAlign: "center", color: "white", fontWeight: 600 }}>{ui.__("appStoreRedirect.landingPage.subTitle")}</pr.P_16_TITLE>
        </div>
        <div style={{ position: "relative", marginRight: isMobile ? "0px" : "56px" }}>
            <img
                style={{ display: "block" }}
                src={require("../../assets/illustration_george_app_new.svg")}
                alt={ui.__("appStoreRedirect.landingPage.headerImage.altText")}>
            </img>
            <img
                style={{ width: 88, height: 88, position: "absolute", bottom: 2, right: 4 }}
                src={require("../../assets/george-app-icon.png")}
                alt={ui.__("appStoreRedirect.landingPage.headerImage.altText")}
            />
        </div>

    </pr.HeaderBackground>
);

const LandingPageFooter = () => (
    <div
        style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "start",
            position: "absolute",
            bottom: 0,
            width: "100%",
            height: FOOTER_HEIGHT,
            backgroundColor: "#2870ED",
        }}
    >
        <img
            src={require("../../assets/george_app_footer_logo.svg")}
            alt={ui.__("appStoreRedirect.landingPage.footer.altText")}
        />
    </div>
);

export const AppStoreRedirectSite = observer(({ store }: { store: AppStoreRedirectStore }) => {
    const isUnknownOs = !externalInterfaceStore.isAndroid && !externalInterfaceStore.isIos && !externalInterfaceStore.isHuawei;
    const isMobile = ui.windowWidth <= 588;

    const webDeeplinkUrl = getWebDeeplinkUrl();

    let redirectUrl: string | undefined;

    if (externalInterfaceStore.isIos) {
        redirectUrl = store.iosStoreUrl;
    } else if (externalInterfaceStore.isAndroid) {
        redirectUrl = store.androidStoreUrl;
    } else if (externalInterfaceStore.isHuawei) {
        redirectUrl = store.appGalleryStoreUrl;
    }

    React.useEffect(() => {
        if (redirectUrl) {
            safeWindowOpen(redirectUrl);
        }

        const queryParams = parse(window.location.search);
        if (queryParams[WEB_QUERY_PARAM]) {
            clearWebDeeplinkFromUrl();
        }
    }, [redirectUrl]);

    if (redirectUrl) {
        return <george.LoadingIndicator isLoading height={ui.windowHeight} />;
    }

    return (
        <george.LandingBackground useWebVersion style={{ boxSizing: "border-box", paddingBottom: FOOTER_HEIGHT + DEFAULT_MARGIN, position: "relative", minHeight: "auto" }}>
            <LandingPageHeader isMobile={isMobile} />
            <div style={{ padding: 24 }}>
                <pr.P_16_CONTENT style={{ textAlign: "start", marginTop: DEFAULT_MARGIN }}>
                    {ui.__("appStoreRedirect.landingPage.description")}
                </pr.P_16_CONTENT>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexWrap: "wrap",
                        marginTop: DEFAULT_MARGIN,
                        gap: 15
                    }}
                >
                    {(externalInterfaceStore.isAndroid || isUnknownOs) &&
                        <a
                            target="_blank"
                            href={store.androidStoreUrl}
                            role="button"
                            title={ui.__("common.download.playStore")}
                        >
                            <img alt={ui.__("appStoreRedirect.landingPage.googlePlayStoreButton.altText")} style={{ height: 50 }} src={externalInterfaceStore.androidStoreIconUrl} />
                        </a>
                    }
                    {(externalInterfaceStore.isIos || isUnknownOs) &&
                        <a
                            target="_blank"
                            href={store.iosStoreUrl}
                            role="button"
                            title={ui.__("common.download.appStore")}
                        >
                            <img alt={ui.__("appStoreRedirect.landingPage.appStoreButton.altText")} style={{ height: 50 }} src={externalInterfaceStore.iosStoreIconUrl} />
                        </a>
                    }
                    {(externalInterfaceStore.isHuawei || isUnknownOs) &&
                        <a
                            target="_blank"
                            href={store.appGalleryStoreUrl}
                            role="button"
                            title={ui.__("common.download.appGallery")}
                        >
                            <img alt={ui.__("appStoreRedirect.landingPage.appGalleryButton.altText")} style={{ height: 50 }} src={externalInterfaceStore.appGalleryIconUrl} />
                        </a>
                    }
                </div>
                {webDeeplinkUrl && (
                    <div style={{ display: "flex", justifyContent: "center", flexDirection: "column", marginTop: DEFAULT_MARGIN, alignItems: "center", gap: 16 }}>
                        <pr.P_16_TITLE style={{ textAlign: "center", fontWeight: 600 }}>
                            {ui.__("appStoreRedirect.landingPage.continueToGeorgeWeb")}
                        </pr.P_16_TITLE>
                        <GdsFakeActionButton
                            style={{ borderRadius: "8px" }}
                            onClick={() => window.location.href = webDeeplinkUrl}
                        >
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                                <IconGeorgeApp />
                                {ui.__("appStoreRedirect.landingPage.continueToGeorgeWeb.button")}
                            </div>
                        </GdsFakeActionButton>
                    </div>
                )}
            </div>
            <LandingPageFooter />
        </george.LandingBackground>
    );
});



// WEBPACK FOOTER //
// ./src/components/appStoreRedirect/AppStoreRedirectSiteNew.tsx